import React, { useEffect, useId, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import GenericTextField from '../../../../components/GenericTextField/GenericTextField';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { PaymentMethodEnum, PAYMENT_METHOD_LABELS } from '../../../../common/enums/invoicing.enum';
import { PaymentPreviewDto } from '../../../../common/interfaces/invoicing.interface';
import { previewPayment, recordPayment } from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { DEFAULT_CURRENCY, formatMoney, parseMoneyInput, toMoneyInputValue } from '../../../../utilities/helper/money.helper';
import { formatMonthKey, toLocalDateString } from '../../../../utilities/helper/date.helper';

export interface PaymentPanelProps {
  kindergartenId: string;
  childId: string;
  childName: string;
  payerName?: string | null;
  /** When opened from one invoice: its outstanding amount and reference, for the quick pick and placeholder. */
  invoice?: { invoiceNumber: string | null; remaining: number; paymentReference: string | null } | null;
  onClose: () => void;
  onRecorded: () => void;
}

const monthOf = (year: number, month: number) => formatMonthKey(`${year}-${String(month).padStart(2, '0')}`).toLowerCase();

/**
 * Recording money received for a child, with where it goes shown while it is typed.
 *
 * The distribution comes from the server's preview, which runs the rule recording runs:
 * oldest open invoice first, whatever is left is credit for the next one. So what the panel
 * says a payment will do is what it does. The payment is the child's, not the invoice's -
 * opened from an invoice, it still settles the oldest debt first.
 */
const PaymentPanel: React.FC<PaymentPanelProps> = ({ kindergartenId, childId, childName, payerName, invoice, onClose, onRecorded }) => {
  const { t } = useTranslation();
  const amountId = useId();
  const [amountText, setAmountText] = useState('');
  const [paidAt, setPaidAt] = useState(toLocalDateString(new Date()));
  const [method, setMethod] = useState<PaymentMethodEnum>(PaymentMethodEnum.BANK_TRANSFER);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [owed, setOwed] = useState<number | null>(null);
  const [plan, setPlan] = useState<PaymentPreviewDto | null>(null);
  const [planning, setPlanning] = useState(false);
  const [saving, setSaving] = useState(false);

  const amount = parseMoneyInput(amountText);
  const validAmount = typeof amount === 'number' && amount > 0;

  // what the child owes, before anything is typed
  useEffect(() => {
    previewPayment(kindergartenId, childId, 0)
      .then(result => setOwed((result.results?.invoices ?? []).reduce((sum, line) => sum + line.outstandingBefore, 0)))
      .catch(() => setOwed(null));
  }, [kindergartenId, childId]);

  useEffect(() => {
    if (!validAmount) {
      setPlan(null);
      // a preview cancelled by clearing the field never reaches its own finally
      setPlanning(false);
      return undefined;
    }
    let cancelled = false;
    setPlanning(true);
    const timer = setTimeout(() => {
      previewPayment(kindergartenId, childId, amount as number)
        .then(result => !cancelled && setPlan(result.results ?? null))
        .catch(() => !cancelled && setPlan(null))
        .finally(() => !cancelled && setPlanning(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [kindergartenId, childId, amount, validAmount]);

  const save = async () => {
    if (!validAmount) {
      createNotification(MESSAGE_TYPE.ERROR, t('payments.panel.validation.amount'));
      return;
    }
    if (!paidAt) {
      createNotification(MESSAGE_TYPE.ERROR, t('payments.panel.validation.date'));
      return;
    }
    setSaving(true);
    try {
      await recordPayment(kindergartenId, childId, {
        amount: amount as number,
        paidAt,
        method,
        paymentReference: reference.trim() || undefined,
        note: note.trim() || undefined
      });
      createNotification(MESSAGE_TYPE.SUCCESS, t('payments.panel.recorded', { amount: formatMoney(amount as number, { withCurrency: true }), name: childName }));
      onRecorded();
      onClose();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('payments.recordFailed'));
    } finally {
      setSaving(false);
    }
  };

  const quick = [
    invoice && invoice.remaining > 0 && { key: 'invoice', label: t('payments.panel.quickInvoice', { amount: formatMoney(invoice.remaining) }), value: invoice.remaining },
    owed !== null && owed > 0 && { key: 'all', label: t('payments.panel.quickAll', { amount: formatMoney(owed) }), value: owed }
  ].filter((chip): chip is { key: string; label: string; value: number } => !!chip);

  return (
    <Drawer
      anchor="right"
      open
      onClose={saving ? undefined : onClose}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-label': t('payments.panel.ariaLabel', { name: childName }),
          sx: { width: 560, maxWidth: '100vw', display: 'flex', flexDirection: 'column', bgcolor: 'surface.main', color: 'text.primary', borderRight: 'none' }
        } as object
      }}
    >
      <Box sx={{ p: '24px 24px 16px', borderBottom: 1, borderColor: 'line' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('payments.panel.eyebrow')}</Typography>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
              {childName}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              {[
                payerName && t('payments.panel.payer', { name: payerName }),
                owed !== null && t('payments.panel.owes', { amount: formatMoney(owed, { withCurrency: true }) })
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          <IconButton aria-label={t('common.close')} onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 3, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Typography component="label" htmlFor={amountId} sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
            {t('payments.panel.amount')}
          </Typography>
          <TextField
            id={amountId}
            fullWidth
            autoFocus
            value={amountText}
            error={amount === undefined}
            onChange={event => /^[\d.,\s]*$/.test(event.target.value) && setAmountText(event.target.value)}
            slotProps={{
              htmlInput: { inputMode: 'decimal', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 16 } },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{DEFAULT_CURRENCY}</Typography>
                  </InputAdornment>
                )
              }
            }}
            sx={{ '& .MuiOutlinedInput-root': { height: 44, borderRadius: '10px' } }}
          />
          {quick.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {quick.map(chip => (
                <ButtonBase
                  key={chip.key}
                  onClick={() => setAmountText(toMoneyInputValue(chip.value))}
                  sx={{ height: 28, px: 1.25, borderRadius: 999, bgcolor: 'surface.tint', color: 'primary.main', fontSize: 13 }}
                >
                  {chip.label}
                </ButtonBase>
              ))}
            </Stack>
          )}
        </Box>

        <GenericTextField label={t('payments.panel.paidAt')} isDate value={paidAt} onChange={event => setPaidAt(event.target.value)} />

        <Box>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.75 }}>{t('payments.panel.method')}</Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={method}
            onChange={(_, value: PaymentMethodEnum | null) => value && setMethod(value)}
            aria-label={t('payments.panel.method')}
            sx={{
              bgcolor: 'surface.tint',
              p: 0.5,
              borderRadius: '10px',
              '& .MuiToggleButton-root': {
                border: 0,
                borderRadius: '8px !important',
                textTransform: 'none',
                fontSize: 14,
                color: 'text.secondary',
                '&.Mui-selected': { bgcolor: 'surface.main', color: 'primary.main', fontWeight: 600, '&:hover': { bgcolor: 'surface.main' } }
              }
            }}
          >
            {Object.values(PaymentMethodEnum).map(value => (
              <ToggleButton key={value} value={value}>
                {t(PAYMENT_METHOD_LABELS[value])}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <GenericTextField
          label={t('payments.panel.reference')}
          value={reference}
          placeholder={invoice?.paymentReference ?? undefined}
          onChange={event => setReference(event.target.value)}
          helperText={t('payments.panel.referenceHint')}
        />
        <GenericTextField label={t('payments.panel.note')} value={note} onChange={event => setNote(event.target.value)} helperText={t('payments.panel.noteHint')} />

        <Box component="section" aria-label={t('payments.panel.howTitle')} sx={{ bgcolor: 'surface.tint', borderRadius: 4, p: '16px 20px' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>
              {t('payments.panel.howTitle')}
            </Typography>
            {planning && <CircularProgress size={14} />}
          </Stack>
          {!validAmount || !plan ? (
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('payments.panel.howEmpty')}</Typography>
          ) : (
            <Stack component="ol" spacing={1.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {plan.invoices.map(line => (
                <Box component="li" key={line.invoiceId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 14 }}>
                      {line.invoiceNumber ?? '—'} · {monthOf(line.periodYear, line.periodMonth)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: line.applied === 0 ? 'text.secondary' : line.outstandingAfter === 0 ? 'success.main' : 'warning.main'
                      }}
                    >
                      {line.applied === 0
                        ? t('payments.panel.getsNothing')
                        : line.outstandingAfter === 0
                          ? t('payments.panel.closes')
                          : t('payments.panel.leaves', { amount: formatMoney(line.outstandingAfter) })}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatMoney(line.applied)}
                  </Typography>
                </Box>
              ))}
              {plan.overpayment > 0 && (
                <Box component="li" sx={{ borderTop: 1, borderColor: 'line', pt: 1.25 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {t('payments.panel.overpayment', { amount: formatMoney(plan.overpayment, { withCurrency: true }) })}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ p: '16px 24px', borderTop: 1, borderColor: 'line' }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('payments.panel.footer')}</Typography>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontSize: 16, borderRadius: '10px' }}>
            {t('common.cancel')}
          </Button>
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            label={validAmount ? t('payments.panel.submit', { amount: formatMoney(amount as number, { withCurrency: true }) }) : t('payments.panel.submitEmpty')}
            onAsyncAction={save}
            disabled={saving || !validAmount || !paidAt}
          />
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default PaymentPanel;
